import type { KryspaceRenderManifest, RenderManifestQueueItem } from '../../../types/kryspaceRenderManifest';
import { fixUtf8Mojibake } from '../fixUtf8Mojibake';
import type { GuidanceBacklogEntry, GuidanceBacklogStatus, GuidanceProposalKind } from './guidanceBacklogHelpers';

function rowField(row: RenderManifestQueueItem, key: string): unknown {
  return (row as RenderManifestQueueItem & Record<string, unknown>)[key];
}

function normalizeBacklogStatus(phrase: string): GuidanceBacklogStatus {
  const p = phrase.trim().toLowerCase();
  if (p === 'adopted' || p === 'accepted') return 'adopted';
  if (p === 'declined' || p === 'rejected') return 'declined';
  if (p === 'review' || p === 'in_review') return 'review';
  return 'submitted';
}

function normalizeKind(raw: unknown): GuidanceProposalKind {
  const k = String(raw ?? 'strategy').toLowerCase();
  return k === 'quick_win' || k === 'quick win' ? 'quick_win' : 'strategy';
}

/** Manifest-first backlog proposals from guidance-backlog-proposals section. */
export function parseGuidanceBacklogFromManifest(
  manifest: KryspaceRenderManifest
): GuidanceBacklogEntry[] {
  const section = manifest.sections.find((s) => s.id === 'guidance-backlog-proposals');
  if (!section?.queue?.length) return [];

  const fallbackDate = manifest.generatedAt?.slice(0, 10) ?? '';

  return section.queue.map((row) => {
    const itemId =
      (typeof row.itemId === 'string' && row.itemId) ||
      (typeof rowField(row, 'item_id') === 'string' ? (rowField(row, 'item_id') as string) : '');
    const title = fixUtf8Mojibake(
      (typeof row.title === 'string' && row.title) ||
        (typeof rowField(row, 'name') === 'string' ? (rowField(row, 'name') as string) : itemId)
    );
    const statusPhrase =
      (typeof row.statusPhrase === 'string' && row.statusPhrase) ||
      (typeof rowField(row, 'status_phrase') === 'string'
        ? (rowField(row, 'status_phrase') as string)
        : 'submitted');
    const submittedAt =
      (typeof rowField(row, 'submittedAt') === 'string' && (rowField(row, 'submittedAt') as string).slice(0, 10)) ||
      (typeof rowField(row, 'submitted_at') === 'string'
        ? (rowField(row, 'submitted_at') as string).slice(0, 10)
        : fallbackDate);

    return {
      id: itemId,
      title,
      kind: normalizeKind(rowField(row, 'kind')),
      status: normalizeBacklogStatus(statusPhrase),
      submittedAt,
      source: 'manifest',
    };
  });
}
